import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { GameService } from '../services/gameService';
import { useMobileDetection } from '../hooks/useMobileDetection';
import { colors } from '../constants/colors';

function GameRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [gameData, setGameData] = useState(null);
  const [gameState, setGameState] = useState('waiting');
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [selectedMultipleAnswers, setSelectedMultipleAnswers] = useState([]);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [questionTimer, setQuestionTimer] = useState(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [showFirstCorrectNotification, setShowFirstCorrectNotification] = useState(false);

  
  // MOBILE DETECTION
  const { isMobile } = useMobileDetection();
  
  // FIREBASE GAME SERVICE
  const gameService = new GameService();
  
  // GET PLAYER NAME FROM LOCATION STATE OR LOCAL STORAGE
  const playerName = location.state?.playerName || localStorage.getItem('playerName');
  
  useEffect(() => {
    if (playerName) {
      localStorage.setItem('playerName', playerName);
    }
  }, [playerName]);
  
  useEffect(() => {
    if (!playerName) {
      navigate('/');
      return;
    }
  }, [playerName, navigate]);

  //  timer 
  useEffect(() => {
    let timerInterval;
    
    if (gameState === 'playing' && gameData?.questionStartTime) {
      const updateTimer = () => {
        const now = Date.now();
        const elapsed = Math.floor((now - gameData.questionStartTime) / 1000);
        const timePerQuestion = gameData.settings?.timePerQuestion || 120;
        const remaining = Math.max(0, timePerQuestion - elapsed);
        
        setQuestionTimer(remaining);
        
        if (remaining === 0 && isHost && !gameData.transitioning) {
          console.log('⏰ Time\'s up! Advancing to next question...');
          gameService.advanceQuestion(roomId);
        }
      };
      
      updateTimer();
      timerInterval = setInterval(updateTimer, 1000);
    }
    
    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [gameState, gameData?.questionStartTime, gameData?.settings?.timePerQuestion, isHost, roomId, gameData?.transitioning]);

  // Reset scroll indicator when question changes
  useEffect(() => {
    setShowScrollIndicator(true);
  }, [gameData?.currentQuestion]);


// First to Answer Correctly logic
// Add a ref to track shown notifications
const shownNotifications = useRef(new Set());

useEffect(() => {
  const questionKey = `${gameData?.currentQuestion}-${playerName}`;
  const hasFirstCorrect = gameData?.answers?.[gameData?.currentQuestion]?.[playerName]?.firstCorrect;
  
  if (hasFirstCorrect && !shownNotifications.current.has(questionKey)) {
    console.log('🎉 Showing first correct notification for first time!');
    shownNotifications.current.add(questionKey);
    setShowFirstCorrectNotification(true);
    setTimeout(() => setShowFirstCorrectNotification(false), 3000);
  }
}, [gameData?.answers, gameData?.currentQuestion, playerName]);


  // Handle answer selection functions 
  const handleAnswerSelect = async (answer) => {
    if (hasAnswered || questionTimer === 0) return;
    
    const currentQuestion = gameData.questions?.[gameData.currentQuestion];
    
    if (currentQuestion.type === 'single' || currentQuestion.type === 'boolean') {
      console.log(`🔘 Answer selected: ${answer}`);
      setSelectedAnswer(answer);
      setHasAnswered(true);
      
      try {
        await gameService.submitAnswer(roomId, playerName, answer, gameData.currentQuestion);
        console.log('✅ Answer submitted successfully');
      } catch (error) {
        console.error('❌ Error submitting answer:', error);
        setError('Failed to submit answer');
      }
    }
  };

  const handleMultipleAnswerToggle = (answer) => {
    if (hasAnswered || questionTimer === 0) return;
    
    setSelectedMultipleAnswers(prev => {
      if (prev.includes(answer)) {
        return prev.filter(a => a !== answer);
      } else {
        return [...prev, answer];
      }
    });
  };

  const submitMultipleAnswers = async () => {
    if (hasAnswered || questionTimer === 0 || selectedMultipleAnswers.length === 0) return;
    
    console.log(`🔘 Multiple answers selected: ${selectedMultipleAnswers.join(', ')}`);
    setHasAnswered(true);
    
    try {
      await gameService.submitAnswer(roomId, playerName, selectedMultipleAnswers, gameData.currentQuestion);
      console.log('✅ Multiple answers submitted successfully');
    } catch (error) {
      console.error('❌ Error submitting answers:', error);
      setError('Failed to submit answers');
    }
  };

  // subscription useEffect
  useEffect(() => {
    if (!roomId || !playerName) return;
    
    let previousQuestionIndex = null;
    
    const unsubscribe = gameService.subscribeToGame(roomId, (data) => {
      if (data) {
        console.log('🔄 Game data updated');
        setGameData(data);
        setGameState(data.state || 'waiting');
        setIsHost(data.host === playerName);

        if (data.state === 'playing' && data.currentQuestion !== undefined) {
          const currentQ = data.currentQuestion;
          
          if (previousQuestionIndex !== null && previousQuestionIndex !== currentQ) {
            console.log('📝 New question detected, resetting answer states');
            setHasAnswered(false);
            setSelectedAnswer('');
            setSelectedMultipleAnswers([]);
          }
          
          previousQuestionIndex = currentQ;
          
          const playerAnswers = data.answers?.[currentQ] || {};
          const playerHasAnswered = playerAnswers[playerName] !== undefined;
          
          if (playerHasAnswered && !hasAnswered) {
            setHasAnswered(true);
            const currentQuestion = data.questions?.[currentQ];
            if (currentQuestion?.type === 'multiple') {
              setSelectedMultipleAnswers(playerAnswers[playerName]);
            } else {
              setSelectedAnswer(playerAnswers[playerName]);
            }
          }
        }
      }
    });

    return () => {
      unsubscribe();
      gameService.cleanup();
    };
  }, [roomId, playerName]); // eslint-disable-line react-hooks/exhaustive-deps


  // Bell sound notification - useEffect 
  useEffect(() => {
    if (gameData?.state === 'playing' && 
        gameData?.currentQuestion !== undefined && 
        gameData?.currentQuestion > 0) {
      
      const playBell = async () => {
        try {
          const audio = new Audio('/sounds/bell.wav');
          audio.volume = 0.2;
          await audio.play();
          console.log('✅ Bell played successfully!');
        } catch (err) {
          console.log('Bell sound could not play:', err.message);
        }
      };

      playBell(); 
    }
  }, [gameData?.currentQuestion, gameData?.state]);


// Manual test function
useEffect(() => {
  window.testBellSound = async () => {
    console.log('🧪 Manual bell test...');
    try {
      const audio = new Audio('/sounds/bell.wav');
      audio.volume = 0.2;
      console.log('🧪 Manual test - attempting play...');
      await audio.play();
      console.log('✅ Manual test - sound played!');
    } catch (err) {
      console.error('❌ Manual test failed:', err);
    }
  };
  
  return () => {
    delete window.testBellSound;
  };
}, []);


// Calculate if all players have answered
const allPlayersAnswered = gameData?.state === 'playing' && 
  gameData?.players && 
  gameData?.answers?.[gameData.currentQuestion] &&
  Object.keys(gameData.players).length > 0 &&
  Object.keys(gameData.players).every(player => 
    gameData.answers[gameData.currentQuestion][player] !== undefined
  ) || false;  // Force explicit false instead of undefined

// Calculate notification height for layout adjustments
const notificationHeight = allPlayersAnswered ? 50 : 0;

// Review state changing
console.log('=== LAYOUT DEBUG ===');
console.log('gameState:', gameState);
console.log('allPlayersAnswered:', allPlayersAnswered);
console.log('notificationHeight:', notificationHeight);
console.log('hasAnswered:', hasAnswered);
console.log('questionTimer:', questionTimer);
console.log('gameData.currentQuestion:', gameData?.currentQuestion);
console.log('==================');

const handleStartGame = async () => {
    try {
      setError('');
      await gameService.startGame(roomId);
      console.log('🎮 Game started!');
    } catch (error) {
      console.error('❌ Error starting game:', error);
      setError('Failed to start game: ' + error.message);
    }
  };

  const baseContainerStyle = {
  height: `calc(100dvh - ${notificationHeight}px)`,

  backgroundColor: colors.outerBackground,
  color: colors.textOnDark,
  padding: isMobile ? '10px' : '20px',
  paddingTop: isMobile ? 'max(10px, env(safe-area-inset-top))' : '20px',
  paddingBottom: isMobile ? 'max(10px, env(safe-area-inset-bottom))' : '20px',
  paddingLeft: isMobile ? 'max(10px, env(safe-area-inset-left))' : '20px',
  paddingRight: isMobile ? 'max(10px, env(safe-area-inset-right))' : '20px',
  fontFamily: 'Arial, sans-serif',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  transition: 'height 0.3s ease-in-out'
};


  const handleNextQuestion = async () => {
    try {
      await gameService.advanceQuestion(roomId);
      console.log('➡️ Advanced to next question');
    } catch (error) {
      console.error('❌ Error advancing question:', error);
      setError('Failed to advance question');
    }
  };

// LOADING STATE
if (!gameData) {
  return (
    <div style={{
      ...baseContainerStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '20px' }}>🎯</div>
        <div>Loading game room...</div>
      </div>
    </div>
  );
}

  // WAITING ROOM STATE (unchanged)
  if (gameState === 'waiting') {
    return (
      <div style={baseContainerStyle}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          color: colors.textOnLight 
          }}>
          🎯 Trivia Game Room
        </h1>
          
<div style={{ 
  fontSize: '18px', 
  marginBottom: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  color: colors.textOnLight
}}>
  Room Code: <strong style={{ 
    backgroundColor: colors.innerBackground,
    padding: '5px 10px',
    borderRadius: '5px',
    fontFamily: 'monospace',
    color: colors.textOnDark
  }}>{roomId}</strong>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(roomId).then(() => {
                    const btn = document.getElementById('copy-btn');
                    const originalText = btn.innerText;
                    btn.innerText = '✓ Copied!';
                    btn.style.backgroundColor = '#27ae60';
                    setTimeout(() => {
                      btn.innerText = originalText;
                      btn.style.backgroundColor = '#3498db';
                    }, 2000);
                  }).catch(err => {
                    console.error('Failed to copy:', err);
                    alert('Failed to copy code. Please copy manually.');
                  });
                }}
                id="copy-btn"
                style={{
                  backgroundColor: colors.accent,
                  color: colors.textOnDark,
                  border: 'none',
                  padding: '5px 15px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background-color 0.3s ease'
                }}
              >
                📋 Copy
              </button>
            </div>
            <div style={{ fontSize: '16px', opacity: 0.8 }}>
              Share this code with friends to join!
            </div>
          </div>

          <div style={{
            backgroundColor: colors.innerBackground,
            padding: '30px',
            borderRadius: '10px',
            marginBottom: '30px'
          }}>
            <h3 style={{ marginTop: 0 }}>👥 Players ({Object.keys(gameData.players || {}).length})</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {Object.entries(gameData.players || {}).map(([name, player]) => (
                <div key={name} style={{
                  backgroundColor: player.ready ? '#27ae60' : '#e74c3c',
                  padding: '8px 15px',
                  borderRadius: '20px',
                  fontSize: '14px'
                }}>
                  {name} {isHost && gameData.host === name && '👑'} {player.ready ? '✅' : '⏳'}
                </div>
              ))}
            </div>
          </div>

          {isHost && (
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleStartGame}
                style={{
                  backgroundColor: colors.accent,
                  color: colors.textOnDark,
                  border: 'none',
                  padding: '15px 30px',
                  fontSize: '18px',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }}
              >
                🚀 Start Game
              </button>
            </div>
          )}

          {!isHost && (
            <div style={{ textAlign: 'center', fontSize: '16px', opacity: 0.8, color: colors.textOnLight }}>
              Waiting for host to start the game...
            </div>
          )}

          {error && (
            <div style={{
              backgroundColor: colors.error,
              color: colors.textOnDark,
              padding: '10px',
              borderRadius: '5px',
              marginTop: '20px',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}
        </div>
    );
  }

  // PLAYING STATE - MOBILE OPTIMIZED
if (gameState === 'playing') {
  const currentQuestion = gameData.questions?.[gameData.currentQuestion];
  const currentQuestionIndex = gameData.currentQuestion || 0;
  const totalQuestions = gameData.questions?.length || 0;
  const answers = gameData.answers?.[currentQuestionIndex] || {};
  const playerCount = Object.keys(gameData.players || {}).length;
  const answeredCount = Object.keys(answers).length;
  const allAnswered = gameData.allAnswered || false;

  if (!currentQuestion) {
    return (
      <div style={{
        ...baseContainerStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div>Loading question...</div>
      </div>
    );
  }

  const needsScroll = isMobile && currentQuestion.answers?.length > 4;

  return (
    <div style={baseContainerStyle}>

      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: `${notificationHeight}px`,
        backgroundColor: colors.accent,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isMobile ? '14px' : '16px',
        fontWeight: 'bold',
        zIndex: 1000,
        transform: allPlayersAnswered ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.3s ease-in-out',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
      }}>
      All players answered! Moving to next question...
    </div>
       {showFirstCorrectNotification && (
      <div style={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: colors.success,
        color: 'white',
        padding: '10px 20px',
        borderRadius: '10px',
        zIndex: 1000,
        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
        fontWeight: 'bold'
      }}>
        🎉 First Correct! +25 Bonus Points!
      </div>
    )}
      {/* Fixed notification at top if all players answered */}
      {allAnswered && questionTimer > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: isMobile ? '8px 10px' : '12px',
          paddingTop: isMobile ? 'max(8px, env(safe-area-inset-top))' : '12px',
          backgroundColor: colors.warning,
          textAlign: 'center',
          color: colors.textOnDark,
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: 'bold',
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          ⏳ All players answered! Next in {questionTimer}s
        </div>
      )}
      
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: allAnswered && questionTimer > 0 ? (isMobile ? '45px' : '60px') : '0',
        
      }}>
        
        {/* Add padding top when notification is showing */}
        <div style={{ 
          paddingTop: allAnswered && questionTimer > 0 ? (isMobile ? '40px' : '50px') : '0',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: '20px',
          minHeight: 0
        }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* QUESTION HEADER */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: isMobile ? '20px' : '30px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '10px',
            flexShrink: 0 // Prevent header from shrinking
          }}>
            <div>
              <h2 style={{ 
                margin: 0,
                fontSize: isMobile ? '1.2rem' : '1.5rem'
              }}>
                Question {currentQuestionIndex + 1} of {totalQuestions}
              </h2>
              {currentQuestion.category && (
                <span style={{
                  backgroundColor: colors.accent,
                  color: colors.textOnDark,
                  padding: '4px 12px',
                  borderRadius: '15px',
                  fontSize: isMobile ? '12px' : '14px',
                  marginTop: '5px',
                  display: 'inline-block'
                }}>
                  {currentQuestion.category}
                </span>
              )}
            </div>
            <div style={{
              backgroundColor: questionTimer <= 10 ? '#e74c3c' : '#f39c12',
              padding: isMobile ? '8px 16px' : '10px 20px',
              borderRadius: '25px',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ⏱️ {questionTimer}s
            </div>
          </div>

          {/* QUESTION */}
          <div style={{
            backgroundColor: colors.innerBackground,
            padding: isMobile ? '20px' : '30px',
            borderRadius: '15px',
            marginBottom: isMobile ? '20px' : '30px',
            textAlign: 'center',
            flexShrink: 0 // Prevent question from shrinking
          }}>
            <h3 style={{
              fontSize: isMobile ? '18px' : '24px',
              lineHeight: '1.4',
              margin: 0
            }}>
              {currentQuestion.question}
            </h3>
          </div>
          </div>
          </div>
          
          <div style={{
            overflow: 'hidden', 
            WebkitOverflowScrolling: 'touch',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column', 
            paddingBottom: '20px'
          }}>
          {/* ANSWER OPTIONS - MOBILE OPTIMIZED */}
          {currentQuestion.type === 'boolean' ? (
            // TRUE/FALSE BUTTONS
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: isMobile ? '15px' : '20px',
              marginBottom: '30px',
              maxWidth: '600px',
              margin: '0 auto 30px',
            }}>
              {['True', 'False'].map((answer) => {
                const isSelected = selectedAnswer === answer;
                const isCorrect = answer === currentQuestion.correct_answer;
                const showResults = hasAnswered || questionTimer === 0;

          let buttonStyle = {
            backgroundColor: answer === 'True' ? '#27ae60' : '#e74c3c',
            color: colors.textOnDark,
            border: '3px solid transparent',
            padding: isMobile ? '15px 10px' : '20px 15px',  // SMALLER PADDING
            borderRadius: '10px',
            fontSize: isMobile ? '16px' : '18px',           // SMALLER FONT
            fontWeight: 'bold',
            cursor: hasAnswered ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
            textAlign: 'center',
            maxHeight: isMobile ? '60px' : '70px',          // ADD MAX HEIGHT
            display: 'flex',                                // ADD FLEX
            alignItems: 'center',                          // CENTER CONTENT
            justifyContent: 'center',                      // CENTER CONTENT
            opacity: (!showResults && !isSelected) ? 0.8 : 1
          };

                if (showResults) {
                  if (isCorrect) {
                    buttonStyle.border = '3px solid #f39c12';
                    buttonStyle.boxShadow = '0 0 20px rgba(243, 156, 18, 0.5)';
                  } else if (isSelected && !isCorrect) {
                    buttonStyle.opacity = 0.5;
                  }
                }

                return (
                  <button
                    key={answer}
                    onClick={() => !hasAnswered && handleAnswerSelect(answer)}
                    style={buttonStyle}
                    disabled={hasAnswered || questionTimer === 0}
                  >
                    {answer}
                  </button>
                );
              })}
            </div>
          ) : currentQuestion.type === 'multiple' ? (
            // MULTIPLE CHOICE CHECKBOXES
            <div style={{ position: 'relative' }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: isMobile ? '14px' : '16px',
                fontStyle: 'italic',
                opacity: 0.9
              }}>
                Select all that apply:
              </div>
              <div 
                id="answers-container"
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: isMobile ? '10px' : '15px',
                  marginBottom: '20px',
                  maxHeight: needsScroll ? '40vh' : 'auto',
                  overflowY: needsScroll ? 'auto' : 'visible',
                  paddingRight: needsScroll ? '5px' : '0'
                }}
                onScroll={(e) => {
                  if (e.target.scrollTop > 10) {
                    setShowScrollIndicator(false);
                  }
                }}
              >
                {currentQuestion.answers?.map((answer, index) => {
                  const isSelected = selectedMultipleAnswers.includes(answer);
                  const isCorrect = currentQuestion.correct_answers?.includes(answer);
                  const showResults = hasAnswered || questionTimer === 0;

                  let optionStyle = {
                    backgroundColor: colors.innerBackground,
                    color: colors.textOnDark,
                    border: '3px solid transparent',
                    padding: isMobile ? '15px' : '20px',
                    borderRadius: '10px',
                    fontSize: isMobile ? '14px' : '16px',
                    cursor: hasAnswered ? 'default' : 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  };

                  if (showResults) {
                    if (isCorrect) {
                      optionStyle.backgroundColor = '#27ae60';
                      optionStyle.border = '3px solid #2ecc71';
                    } else if (isSelected && !isCorrect) {
                      optionStyle.backgroundColor = '#e74c3c';
                      optionStyle.border = '3px solid #c0392b';
                    }
                  } else if (isSelected) {
                    optionStyle.backgroundColor = '#3498db';
                    optionStyle.border = '3px solid #2980b9';
                  }

                  return (
                    <label
                      key={index}
                      style={optionStyle}
                      onClick={(e) => {
                        e.preventDefault();
                        !hasAnswered && handleMultipleAnswerToggle(answer);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        disabled={hasAnswered || questionTimer === 0}
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: hasAnswered ? 'default' : 'pointer'
                        }}
                      />
                      {answer}
                    </label>
                  );
                })}
              </div>
              
              {/* Scroll indicator */}
              {needsScroll && showScrollIndicator && (
                <div style={{
                  position: 'absolute',
                  bottom: '80px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  animation: 'bounce 2s infinite',
                  pointerEvents: 'none'
                }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '5px'
                  }}>
                    Scroll for more
                  </div>
                  <div style={{ fontSize: '20px' }}>⬇️</div>
                </div>
              )}
              
              {!hasAnswered && (
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={submitMultipleAnswers}
                    disabled={selectedMultipleAnswers.length === 0}
                    style={{
                      backgroundColor: selectedMultipleAnswers.length > 0 ? '#3498db' : '#7f8c8d',
                      color: colors.textOnDark,
                      border: 'none',
                      padding: isMobile ? '12px 30px' : '15px 40px',
                      fontSize: isMobile ? '16px' : '18px',
                      borderRadius: '10px',
                      cursor: selectedMultipleAnswers.length > 0 ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Submit Answer{selectedMultipleAnswers.length > 1 ? 's' : ''}
                  </button>
                </div>
              )}
            </div>
          ) : (
            // SINGLE CHOICE BUTTONS - MOBILE OPTIMIZED
            <div style={{ position: 'relative' }}>
              <div 
                id="answers-container"
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: isMobile ? '10px' : '15px',
                  marginBottom: '30px',
                  maxHeight: needsScroll ? '50vh' : 'auto',
                  overflowY: needsScroll ? 'auto' : 'visible',
                  paddingRight: needsScroll ? '5px' : '0'
                }}
                onScroll={(e) => {
                  if (e.target.scrollTop > 10) {
                    setShowScrollIndicator(false);
                  }
                }}
              >
                {currentQuestion.answers?.map((answer, index) => {
                  const optionLabels = ['A', 'B', 'C', 'D', 'E', 'F'];
                  const isSelected = selectedAnswer === answer;
                  const isCorrect = answer === currentQuestion.correct_answer;
                  const showResults = hasAnswered || questionTimer === 0;

                  let buttonStyle = {
                    backgroundColor: colors.innerBackground,
                    color: colors.textOnDark,
                    border: '3px solid transparent',
                    padding: isMobile ? '15px 10px' : '20px',
                    borderRadius: '10px',
                    fontSize: isMobile ? '14px' : '16px',
                    cursor: hasAnswered ? 'default' : 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'left',
                    minHeight: isMobile ? '70px' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  };

                  if (showResults) {
                    if (isCorrect) {
                      buttonStyle.backgroundColor = '#27ae60';
                      buttonStyle.border = '3px solid #2ecc71';
                    } else if (isSelected && !isCorrect) {
                      buttonStyle.backgroundColor = '#e74c3c';
                      buttonStyle.border = '3px solid #c0392b';
                    }
                  } else if (isSelected) {
                    buttonStyle.backgroundColor = '#3498db';
                    buttonStyle.border = '3px solid #2980b9';
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => !hasAnswered && handleAnswerSelect(answer)}
                      style={buttonStyle}
                      disabled={hasAnswered || questionTimer === 0}
                    >
                      <div>
                        <strong>{optionLabels[index]})</strong>
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        {answer}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {/* Scroll indicator for single choice */}
              {needsScroll && showScrollIndicator && (
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  animation: 'bounce 2s infinite',
                  pointerEvents: 'none'
                }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'rgba(255, 255, 255, 0.7)',
                    marginBottom: '5px'
                  }}>
                    Scroll for more
                  </div>
                  <div style={{ fontSize: '20px' }}>⬇️</div>
                </div>
              )}
            </div>
          )}      
          


          {/* SHOW CORRECT ANSWER WHEN TIME IS UP OR ANSWERED */}
          {(hasAnswered || questionTimer === 0) && (
            <div style={{
              backgroundColor: colors.innerBackground,
              color: colors.textOnDark,
              padding: isMobile ? '12px' : '15px',
              borderRadius: '10px',
              textAlign: 'center',
              marginBottom: '20px',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 'bold',
              border: '2px solid #f39c12'
            }}>
              {currentQuestion.type === 'multiple' ? (
                <>
                  Correct answers: {currentQuestion.correct_answers?.join(', ') || 'None'}
                </>
              ) : (
                <>
                  {gameData.answers?.[gameData.currentQuestion]?.[playerName]?.isCorrect ? '✅ Correct!' : '❌ Incorrect.'}
                  The answer was: {currentQuestion.correct_answer}
                </>
              )}
            </div>
          )}
            {/* SHOW WHEN ALL PLAYERS ANSWERED */}
   
          {/* PLAYER STATUS - MOBILE OPTIMIZED */}
          <div style={{
            backgroundColor: colors.innerBackground,
            padding: isMobile ? '15px' : '20px',
            borderRadius: '10px',
            marginBottom: '20px',
          }}>
            <div style={{ 
              fontSize: isMobile ? '16px' : '18px', 
              marginBottom: '15px' 
            }}>
              Players answered: {answeredCount}/{playerCount}
            </div>
            
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: isMobile ? '8px' : '10px' 
            }}>
              {Object.entries(gameData.players || {}).map(([name, player]) => {
                const hasPlayerAnswered = answers[name] !== undefined;
                return (
                  <div key={name} style={{
                    backgroundColor: hasPlayerAnswered ? '#27ae60' : '#7f8c8d',
                    padding: isMobile ? '6px 12px' : '8px 15px',
                    borderRadius: '20px',
                    fontSize: isMobile ? '12px' : '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    {name} {hasPlayerAnswered ? '✅' : '❌'}
                  </div>
                );
              })}
            </div>
          </div>

          {/* CURRENT SCORES - MOBILE OPTIMIZED */}
          <div style={{
            backgroundColor: colors.innerBackground,
            padding: isMobile ? '15px' : '20px',
            borderRadius: '10px'
          }}>
            <h4 style={{ 
              marginTop: 0,
              fontSize: isMobile ? '16px' : '18px'
            }}>
              {isHost ? 'Current Scores:' : 'Your Score:'}
            </h4>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: isMobile ? '10px' : '15px' 
            }}>
              {isHost ? (
                // Host sees all scores
                Object.entries(gameData.players || {})
                  .sort(([,a], [,b]) => (b.score || 0) - (a.score || 0))
                  .map(([name, player]) => (
                    <div key={name} style={{
                      backgroundColor: colors.outerBackground,
                      padding: isMobile ? '8px 12px' : '10px 15px',
                      borderRadius: '8px',
                      fontSize: isMobile ? '14px' : '16px',
                      border: name === playerName ? '2px solid #3498db' : 'none',
                      color: colors.textOnLight
                    }}>
                      {name}: <strong>{player.score || 0} pts</strong>
                    </div>
                  ))
              ) : (
                // Regular players only see their own score
                <div style={{
                  backgroundColor: colors.outerBackground,
                  padding: isMobile ? '12px 20px' : '15px 25px',
                  borderRadius: '8px',
                  fontSize: isMobile ? '16px' : '18px',
                  border: '2px solid #3498db',
                  color: colors.textOnLight
                }}>
                  <strong>{gameData.players[playerName]?.score || 0} points</strong>
                </div>
              )}
            </div>
          </div>

          {/* HOST CONTROLS */}
          {isHost && questionTimer === 0 && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={handleNextQuestion}
                style={{
                  backgroundColor: colors.accent,
                  color: colors.textOnDark,
                  border: 'none',
                  padding: isMobile ? '12px 25px' : '15px 30px',
                  fontSize: isMobile ? '16px' : '18px',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }}
              >
                ➡️ Next Question
              </button>
            </div>
          )}

          {error && (
            <div style={{
              backgroundColor: colors.error,
              color: colors.textOnDark,
              padding: '10px',
              borderRadius: '5px',
              marginTop: '20px',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}
          
          


          </div>
        </div>
      </div>
    );
  }

  // FINISHED STATE
  if (gameState === 'finished') {
    const finalScores = Object.entries(gameData.players || {})
      .sort(([,a], [,b]) => (b.score || 0) - (a.score || 0));

    return (
      <div style={baseContainerStyle}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{ fontSize: isMobile ? '36px' : '48px', marginBottom: '30px' }}>🎉</h1>
          <h2 style={{ marginBottom: '40px' }}>Game Finished!</h2>
          
          <div style={{
            backgroundColor: colors.innerBackground,
            padding: isMobile ? '20px' : '30px',
            borderRadius: '15px',
            marginBottom: '30px'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '25px' }}>🏆 Final Scores</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {finalScores.map(([name, player], index) => (
                <div key={name} style={{
                  backgroundColor: index === 0 ? '#f39c12' : '#2c3e50',
                  padding: isMobile ? '12px 15px' : '15px 20px',
                  borderRadius: '10px',
                  fontSize: isMobile ? '16px' : '18px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>
                    {index === 0 && '👑 '}
                    {index + 1}. {name}
                  </span>
                  <strong>{player.score || 0} pts</strong>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => navigate('/')}
            style={{
              backgroundColor: colors.accent,
              color: colors.textOnDark,
              border: 'none',
              padding: isMobile ? '12px 25px' : '15px 30px',
              fontSize: isMobile ? '16px' : '18px',
              borderRadius: '10px',
              cursor: 'pointer'
            }}
          >
            🏠 Back to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
}



export default GameRoom;

